import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DEPLOYMENT_STATUS, formatEnumLabel } from '../constants/enums';
import { dateKeyForStatus, emptyDeploymentStatusDates } from './DeploymentLifecycleFields';

function slotDecision(approvals, slotIndex) {
  const row = approvals?.[slotIndex];
  if (!row) return null;
  if (row.decision === 'DECLINED') return 'DECLINED';
  if (row.decision === 'APPROVED' || (row.approvedAt && row.decision !== 'DECLINED')) return 'APPROVED';
  if ((row.name || row.email) && row.approvedAt) return 'APPROVED';
  return null;
}

/**
 * Deployment status table from sr_deployment_status.
 * Optional approvalActions: 1st/2nd Approval buttons open the parent dialog.
 */
export default function DeploymentStatusTable({
  deploymentStatus,
  deploymentStatusDates,
  deploymentStatusApprovals,
  deploymentStatusesFromApi,
  showAllStages = false,
  approvalActions = false,
  onApprovalClick,
  actionsDisabled = false,
  canFirstApproval = true,
  canSecondApproval = true,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const dates = deploymentStatusDates || emptyDeploymentStatusDates();

  const rows = useMemo(() => {
    const byStatus = new Map();
    (deploymentStatusesFromApi ?? []).forEach((stage) => {
      if (!stage?.status) return;
      byStatus.set(stage.status, stage);
    });

    return DEPLOYMENT_STATUS.map((status) => {
      const key = dateKeyForStatus(status);
      const apiStage = byStatus.get(status);
      const approvals =
        apiStage?.approvals?.length
          ? apiStage.approvals
          : (deploymentStatusApprovals?.[key] ?? []).filter(
              (a) => (a.name || '').trim() || (a.email || '').trim()
            );
      const stageDate = apiStage?.stageDate || dates[key] || '';
      const isCurrent =
        apiStage?.current === true || (!apiStage && status === deploymentStatus);
      const decisionStatus =
        apiStage?.decisionStatus
        || (approvals.some((a) => a.decision === 'DECLINED')
          ? 'DECLINED'
          : approvals.some((a) => a.decision === 'APPROVED' || a.approvedAt)
            ? 'APPROVED'
            : '');

      return {
        status,
        stageDate,
        isCurrent,
        decisionStatus,
        approvals,
      };
    }).filter((row) => {
      if (showAllStages) return true;
      return (
        row.isCurrent
        || row.stageDate
        || row.decisionStatus
        || (row.approvals?.length ?? 0) > 0
      );
    });
  }, [
    deploymentStatusesFromApi,
    deploymentStatusApprovals,
    deploymentStatusDates,
    deploymentStatus,
    dates,
    showAllStages,
  ]);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No deployment status records yet. Add stage dates on Software Identification first.
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${alpha(primary, isDark ? 0.35 : 0.14)}`,
        borderRadius: 2,
        bgcolor: isDark ? 'background.paper' : alpha(primary, 0.02),
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Deployment status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Stage date</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Current</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Decision</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Approvals / comments</TableCell>
            {approvalActions ? (
              <TableCell sx={{ fontWeight: 700 }} align="right">
                Actions
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const first = slotDecision(row.approvals, 0);
            const second = slotDecision(row.approvals, 1);
            return (
              <TableRow
                key={row.status}
                sx={{
                  bgcolor:
                    row.decisionStatus === 'DECLINED'
                      ? alpha(theme.palette.error.main, isDark ? 0.16 : 0.08)
                      : row.isCurrent
                        ? alpha(theme.palette.secondary.main, isDark ? 0.16 : 0.08)
                        : 'transparent',
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={row.isCurrent ? 700 : 500}>
                    {formatEnumLabel(row.status)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.stageDate || '—'}</Typography>
                </TableCell>
                <TableCell>
                  {row.isCurrent ? (
                    <Chip label="Current" color="secondary" size="small" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {row.decisionStatus === 'DECLINED' ? (
                    <Chip label="Declined" color="error" size="small" />
                  ) : row.decisionStatus === 'APPROVED' ? (
                    <Chip label="Approved" color="success" size="small" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {(row.approvals?.length ?? 0) === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      None
                    </Typography>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={0.25}>
                      {row.approvals.map((a, idx) => (
                        <Typography
                          key={a.id ?? `${row.status}-${idx}`}
                          variant="caption"
                          display="block"
                          color={a.decision === 'DECLINED' ? 'error.main' : 'text.primary'}
                        >
                          {a.decision === 'DECLINED'
                            ? 'Declined'
                            : a.decision === 'APPROVED'
                              ? 'Approved'
                              : 'Recorded'}
                          {': '}
                          {a.name || a.email || 'Approver'}
                          {a.email ? ` · ${a.email}` : ''}
                          {a.approvedAt
                            ? ` · ${String(a.approvedAt).replace('T', ' ').slice(0, 16)}`
                            : ''}
                          {a.comment ? ` · “${a.comment}”` : ''}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </TableCell>
                {approvalActions ? (
                  <TableCell align="right">
                    <Box display="inline-flex" gap={0.75} flexWrap="wrap" justifyContent="flex-end">
                      {[0, 1].map((slotIndex) => {
                        const allowed = slotIndex === 0 ? canFirstApproval : canSecondApproval;
                        if (!allowed) return null;

                        const decision = slotIndex === 0 ? first : second;
                        const label = slotIndex === 0 ? '1st Approval' : '2nd Approval';
                        let buttonLabel = label;
                        if (decision === 'APPROVED') buttonLabel = `${label} ✓`;
                        if (decision === 'DECLINED') buttonLabel = `${label} ✕`;
                        return (
                          <Button
                            key={slotIndex}
                            size="small"
                            variant={decision ? 'contained' : 'outlined'}
                            color={decision === 'DECLINED' ? 'error' : 'secondary'}
                            disabled={actionsDisabled}
                            onClick={() =>
                              onApprovalClick?.({
                                status: row.status,
                                slot: slotIndex + 1,
                                stageDate: row.stageDate || undefined,
                                existingComment: row.approvals?.[slotIndex]?.comment || '',
                              })
                            }
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0, px: 1.25 }}
                          >
                            {buttonLabel}
                          </Button>
                        );
                      })}
                    </Box>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
